import { updateFileUploadStatus } from 'features/ui/ui.slice';
import { SignalRContext } from 'shared/contexts/signalr';
import { FileUploadStatus } from 'shared/typings/file/enums';
import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fileUpdated } from '../files.slice';
import { useFiles } from '../useFiles';
import { useToasts } from 'shared/hooks/useToasts';

interface FacebookAssetError {
  fileId: number;
  message: string;
  fileName: string;
}

export const useFacebookAssetNotification = (): void => {
  const { connection } = useContext(SignalRContext);
  const dispatch = useDispatch();
  const { updateFileUpload } = useFiles();

  const { setErrorToast } = useToasts();

  const handleErrorToasts = (error: FacebookAssetError) => {
    // this is an expected case when a file is already in the library
    // we don't need to show a toast for this
    if ('File is already in the library.' === error.message) {
      return;
    }
    // In function don't think we can access estimated_time_to_regain_access, but can clarify the file affected
    setErrorToast({
      heading: `Error uploading asset: ${error.fileName}`,
      message: `File ID: ${error.fileId}\nError: ${error.message}`,
    });
  };

  useEffect(() => {
    const assetHandler = ({ accountPlatformAssets, errors, accountPlatformName }) => {
      accountPlatformAssets.forEach(({ fileId }) => {
        updateFileUpload(fileId, FileUploadStatus.UPLOADED);
        dispatch(
          fileUpdated({
            data: {
              id: fileId,
              facebookAssetAPNames: accountPlatformName,
            },
          }),
        );
      });
      errors.forEach((error) => {
        const { fileId, message } = error;
        dispatch(
          updateFileUploadStatus({
            id: fileId,
            fileUploadStatus: FileUploadStatus.UPLOAD_FAILED,
            errors: [message],
          }),
        );
        handleErrorToasts(error);
      });
    };

    if (connection) {
      connection.on('newFacebookAsset', assetHandler);
    }

    return function cleanup() {
      connection?.off('newFacebookAsset', assetHandler);
    };
  }, [connection]);
};
